import translations from '../i18n/translations'; import { detectOsLocale, resolveLanguage } from '../i18n/resolveLanguage';
import { showImperativeToast } from '../providers/UIProvider';
import { diagnosticLog } from './diagnosticLogger';
import { resolveExclusiveDeviceName, logLocalOutputShared } from './localOutputLogging';
import { resolveZoneDisplayName } from './zoneDisplayName';
import { useMaestroStore } from '../store/maestroStore';
import {
  exclusiveFallbackDeviceNoticeKey,
  shouldShowExclusiveFallbackDeviceNotice,
} from './usbExclusiveFallbackNotice';
import { DEVICE_BUSY_RETRY_MS, SHARED_FALLBACK_RELEASE_MS, sleep } from './exclusiveFallbackTiming'; import { showSharedModeNotice } from './sharedModeNotice';

export type ExclusiveFallbackReason = 'format' | 'device_busy';

/** Phase 2A connector error prefixes + legacy exclusive strings. */
const RECOVERABLE_EXCLUSIVE_MARKERS = [
  'exclusive_device_busy:',
  'exclusive_format_unsupported:',
  'exclusive_mode_not_allowed:',
  'exclusive_open_timeout:', // #10426-a1 — Connector ≥ v11.0.328 bounded open; shared is expected to work
  'format not supported in exclusive mode',
] as const;

function t(key: string, vars?: Record<string, string | number>): string {
  let lang = 'en';
  try {
    lang = resolveLanguage(localStorage.getItem('maestro_language'), detectOsLocale());
  } catch {
    /* ignore */
  }
  const catalog = translations as Record<string, Record<string, string>>;
  let str = catalog[lang]?.[key] || catalog.en?.[key] || key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = str.split(`{${k}}`).join(String(v));
    }
  }
  return str;
}

export function isExclusiveDeviceBusyError(errMsg: string): boolean {
  const lower = String(errMsg || '').toLowerCase();
  return lower.startsWith('exclusive_device_busy:')
    || /exclusive_init_failed:.*0x800700aa/i.test(lower);
}

/** True when exclusive open failed but shared-mode playback is expected to work. */
export function isRecoverableExclusiveFailure(errMsg: string): boolean {
  const lower = String(errMsg || '').toLowerCase();
  return RECOVERABLE_EXCLUSIVE_MARKERS.some((marker) => lower.includes(marker))
    || isExclusiveDeviceBusyError(errMsg);
}

/** True when connector refused exclusive because bit-perfect gate could not open HOG. */
export function isExclusiveHogUnavailableError(errMsg: string): boolean {
  return String(errMsg || '').toLowerCase().includes('exclusive_hog_unavailable');
}

/** Drop Chromium's setSinkId/HTML5 hold before exclusive Initialize (#841). */
export async function releaseSharedFallbackBeforeExclusive(): Promise<void> {
  const store = useMaestroStore.getState();
  const az = store.discovery?.activeZone as { type?: string } | null | undefined;
  const onSameLocalRenderer = az?.type === 'local-native' || !store.activeConnectorRenderer?.id;
  if (!onSameLocalRenderer) return;

  console.warn('[exclusive] stopping shared fallback before exclusive attempt');
  store.setPlaybackState('loading'); // #10426-a1: "connecting" while exclusive opens — not a paused-looking UI
  store.setRendererHasStream(false);
  store.setUsbOutputPath(null);

  const { TransportCommand } = await import('../transport/TransportCommand');
  TransportCommand.resetLocalAudioElement();
  TransportCommand.pauseLocalAudioElement(); // #963 — never enqueue pause inside playUri mutex
  await TransportCommand.stopExclusiveEngine().catch(() => {});
  await sleep(SHARED_FALLBACK_RELEASE_MS);
}

/** Retry exclusive open once after device_busy before shared fallback. */
export async function exclusivePlayWithDeviceBusyRetry<T>(
  playFn: () => Promise<T>,
): Promise<T> {
  try {
    return await playFn();
  } catch (err: unknown) {
    const msg = String((err as { message?: string })?.message || err || '');
    if (!isExclusiveDeviceBusyError(msg)) throw err;
    await sleep(DEVICE_BUSY_RETRY_MS);
    return await playFn();
  }
}

/** True only when the fatal red banner should be shown (no shared fallback expected). */
export function shouldShowFatalExclusiveError(errMsg: string): boolean {
  return !isRecoverableExclusiveFailure(errMsg);
}

export function exclusiveFallbackReason(errMsg: string): ExclusiveFallbackReason {
  return isExclusiveDeviceBusyError(errMsg) ? 'device_busy' : 'format';
}

export async function runExclusiveSharedFallback(options: {
  errMsg: string;
  track: any;
  zone: any;
  branch: string;
  playShared: () => Promise<void>;
}): Promise<void> {
  const { errMsg, track, zone, branch, playShared } = options; const { decideExclusiveTimeout } = await import('../transport/exclusiveTimeoutFallback');
  const timeoutDecision = await decideExclusiveTimeout(errMsg, branch, track?.id); // #10426-a1: timeout → shared unless the engine is really playing (#10453)
  if (timeoutDecision === 'keep_exclusive' || timeoutDecision === 'superseded') return;
  const store = useMaestroStore.getState();

  console.warn(`[QC] exclusive open failed — falling back to shared (${branch})`, errMsg);
  const fallbackDevice = resolveZoneDisplayName(zone, {
    selectedUsbDac: store.selectedUsbDac,
    discoveredZones: store.discovery?.discoveredZones,
  }) || 'System Audio';
  const exclusiveDevice = resolveExclusiveDeviceName(zone, store.selectedUsbDac) || 'System Audio';
  logLocalOutputShared(exclusiveDevice, `exclusive_open_failed:${errMsg}`);

  const { TransportCommand } = await import('../transport/TransportCommand');
  await TransportCommand.stopExclusiveEngine().catch(() => {});

  diagnosticLog('info', 'playback', 'exclusive shared fallback', {
    branch,
    error: errMsg,
    recoverable: isRecoverableExclusiveFailure(errMsg),
  });

  store.setExclusiveError(null);
  store.setTransportPlaybackError(null);
  store.setUsbOutputPath('shared');

  try {
    await playShared();
    const post = useMaestroStore.getState();
    post.setExclusiveError(null);
    const { noteAudiblePlaybackStarted } = await import('../transport/localNativePlaybackVerify');
    noteAudiblePlaybackStarted(post.currentTrack?.id);
    showSharedModeNotice(timeoutDecision === 'fallback_shared' ? 'exclusive_timeout' : exclusiveFallbackReason(errMsg), { device: fallbackDevice });

    const dac = useMaestroStore.getState().selectedUsbDac;
    const deviceKey = exclusiveFallbackDeviceNoticeKey(dac?.id || fallbackDevice);
    if (shouldShowExclusiveFallbackDeviceNotice(deviceKey)) {
      showImperativeToast(
        t('exclusive.fallback_toast', { device: fallbackDevice }),
        4500,
      );
    }
  } catch (fallbackErr: unknown) {
    const fallbackMsg = String((fallbackErr as { message?: string })?.message || fallbackErr || '');
    diagnosticLog('warn', 'playback', 'exclusive and shared fallback failed', {
      branch,
      exclusiveError: errMsg,
      fallbackError: fallbackMsg,
      device: fallbackDevice,
      recoverable: isRecoverableExclusiveFailure(errMsg),
    });

    const postStore = useMaestroStore.getState();
    postStore.setPlaybackState('paused');
    postStore.setRendererHasStream(false);
    postStore.setTransportPlaybackError(
      t('exclusive.playback_failed_transport', { device: fallbackDevice }),
    );
    throw fallbackErr;
  }
}

/** Clear stale fatal banner at the start of a user-initiated play. */
export function clearExclusiveErrorOnUserPlayStart(): void {
  const store = useMaestroStore.getState();
  store.setExclusiveError(null);
  store.setTransportPlaybackError(null);
}

/** Clear fatal banner once shared/exclusive playback is confirmed live. */
export function clearExclusiveErrorOnPlaybackConfirmed(): void {
  const store = useMaestroStore.getState();
  store.setExclusiveError(null);
  store.setTransportPlaybackError(null);
}
